import { EntityType, Instrument, MarketType, Product, RepoType } from 'api'
import { isEmpty } from 'lodash'
import { isNotNil } from 'utils/predicate'
import {
  canTradeSector,
  doesBasketTypeRequiresTradingEntityPermission,
  isBasketPermittedToBeSblSecondary,
  isBasketTypePermittedToBePrimary,
  isCollateral
} from 'model'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { allEnabledCollateralBasketsSelector } from '../collateralBasketSelectors'
import { allEnabledCollateralsSelector } from '../collateralSelectors'
import { authorisedUserSelector } from '../connectionSelectors'
import { createAllEntitiesSelector, createEntityGetterSelector } from '../entitySelectors'
import { productByProductSectorIdGetterSelector } from '../productSelectors'
import { GlobalState } from 'store'

export const EMPTY_INSTRUMENT_SET_FILTER = (_instrument: Instrument) => false

export enum FilterType {
  AND,
  OR
}

export const instrumentCanBeTradedByAuthorisedUserSelector: Selector<
  GlobalState,
  (marketType?: MarketType | null) => (instrument: Instrument) => boolean
> = createSelector([authorisedUserSelector, createEntityGetterSelector(EntityType.TRADING_ENTITY)], (authorisedUser, getTradingEntity) => {
  const authorisedUserTradingEntities = authorisedUser.tradingEntityDetails.map(d => getTradingEntity(d.tradingEntityId)).filter(isNotNil)
  return marketType => {
    return instrument => {
      return (
        canTradeSector(instrument.productSectorId, authorisedUser.productRights, marketType) &&
        authorisedUserTradingEntities.some(tradingEntity =>
          canTradeSector(instrument.productSectorId, tradingEntity.productRights, marketType)
        )
      )
    }
  }
})

export const tradingEntitiesForOrganisationsGetterSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY)],
  tradingEntities => (organisationIds: string[]) => tradingEntities.filter(te => organisationIds.includes(te.organisationId))
)

export const createInstrumentCanBeTradedByTradingEntitySelector: Selector<
  GlobalState,
  (marketType: MarketType | null, tradingEntityIds: string[], filterType?: FilterType) => (instrument: Instrument) => boolean
> = createSelector([createEntityGetterSelector(EntityType.TRADING_ENTITY)], getTradingEntity => {
  return (marketType, tradingEntityIds, filterType = FilterType.AND) => {
    const tradingEntities = tradingEntityIds.map(getTradingEntity).filter(isNotNil)

    return instrument => {
      if (isEmpty(tradingEntityIds)) {
        return true
      }

      return (
        tradingEntities.length === tradingEntityIds.length &&
        tradingEntities[filterType === FilterType.AND ? 'every' : 'some'](te =>
          canTradeSector(instrument.productSectorId, te.productRights, marketType)
        )
      )
    }
  }
})

export const authorisedUserIsPermissionedToTradeInstrumentSelector = createSelector(
  [authorisedUserSelector, createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  (authorisedUser, getProductSector) => (instrument: Instrument) => {
    const productSector = getProductSector(instrument.productSectorId)

    if (
      isCollateral(instrument) ||
      (productSector?.basketType && !doesBasketTypeRequiresTradingEntityPermission(productSector.basketType))
    ) {
      return true
    }

    return authorisedUser.tradingEntityDetails.some(d => instrument.tradingEntityIds.includes(d.tradingEntityId))
  }
)

export const instrumentIsPermittedToBeSecondarySblSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProductSector => (instrument: Instrument) => {
    const productSector = getProductSector(instrument.productSectorId)
    const basketType = productSector?.basketType

    return !!(!isCollateral(instrument) && basketType && isBasketPermittedToBeSblSecondary(basketType))
  }
)

export const instrumentIsEligibleForSBLSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProductSector => {
    return (instrument: Instrument) => {
      const productSector = getProductSector(instrument.productSectorId)
      return isNotNil(productSector) && productSector.supportedRepoTypes.includes(RepoType.STOCK_BORROWING_LENDING)
    }
  }
)

export const createTradingEntityIsPermissionedToTradeInstrumentSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProductSector => (tradingEntityIds: string[], filterType: FilterType = FilterType.AND) => (instrument: Instrument) => {
    const productSector = getProductSector(instrument.productSectorId)

    if (
      isCollateral(instrument) ||
      tradingEntityIds.length === 0 ||
      (productSector?.basketType && !doesBasketTypeRequiresTradingEntityPermission(productSector.basketType))
    ) {
      return true
    }

    return tradingEntityIds[filterType === FilterType.AND ? 'every' : 'some'](fundId => instrument.tradingEntityIds.includes(fundId))
  }
)

export const createFilteredInstrumentsSelector = <TProps>(
  filtersSelector: Selector<{}, undefined | Array<(t: Instrument) => boolean>, [TProps]>
) =>
  createSelector(
    [
      createSelector([allEnabledCollateralsSelector, allEnabledCollateralBasketsSelector], (allCollaterals, allBaskets) => [
        ...allCollaterals,
        ...allBaskets
      ]),
      filtersSelector
    ],
    (instruments, filters = []) => instruments.filter(instrument => filters.every(filter => filter(instrument)))
  )

export const createInstrumentHasSameMicCodeSelector: Selector<
  GlobalState,
  (product: Product, marketType: MarketType | null) => (instrument: Instrument) => boolean
> = createSelector(productByProductSectorIdGetterSelector, getProduct => (product, marketType) => instrument => {
  const candidateProduct = getProduct(instrument.productSectorId)

  if (MarketType.SECONDARY === marketType) {
    return product?.secondaryMicCode === candidateProduct?.secondaryMicCode
  }

  return product?.micCode === candidateProduct?.micCode
})

export const instrumentIsPermittedToBePrimarySelector: Selector<GlobalState, (instrument: Instrument) => boolean> = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProduct => (instrument: Instrument) => {
    if (isCollateral(instrument)) return true

    const productSector = getProduct(instrument.productSectorId)

    return !!(productSector?.basketType && isBasketTypePermittedToBePrimary(productSector.basketType))
  }
)
